import React from 'react';
import { Link } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';

const CCPAPage = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] bg-clip-text text-transparent">
            California Privacy Rights (CCPA)
          </h1>
          <p className="text-gray-400">
            Your rights under the California Consumer Privacy Act
          </p>
        </div>

        {/* Quick Links */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6 mb-12">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">Quick Actions</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Link 
              to="/do-not-sell" 
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg text-center font-semibold transition-colors"
            >
              Do Not Sell My Data
            </Link>
            <Link 
              to="/privacy-policy" 
              className="bg-[#2f6eb0] hover:bg-[#3d7fcb] text-white px-4 py-3 rounded-lg text-center font-semibold transition-colors"
            >
              Full Privacy Policy
            </Link>
            <a 
              href="mailto:datarequest@electricnow.tv" 
              className="bg-[#1e4a7a] hover:bg-[#2f6eb0] text-white px-4 py-3 rounded-lg text-center font-semibold transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Your California Privacy Rights</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                If you are a California resident, you have specific rights regarding your personal information under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA). This page explains your rights and how to exercise them.
              </p>
              <p className="text-gray-300 leading-relaxed">
                ElectricNOW is committed to protecting your privacy and ensuring you have control over your personal information. We provide this information to help you understand and exercise your rights under California law.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Your CCPA Rights</h2>
            <div className="space-y-6">
              
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">👁️</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-blue-400">Right to Know</h3>
                    <p className="text-gray-300 leading-relaxed mb-3">
                      You have the right to request information about the personal information we collect, use, disclose, and sell about you, including:
                    </p>
                    <ul className="text-gray-300 list-disc list-inside space-y-1">
                      <li>The categories of personal information we collect</li>
                      <li>The sources from which we collect personal information</li>
                      <li>The business or commercial purposes for collecting personal information</li>
                      <li>The categories of third parties with whom we share personal information</li>
                      <li>The specific pieces of personal information we have collected about you</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🚫</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-red-400">Right to Delete</h3>
                    <p className="text-gray-300 leading-relaxed mb-3">
                      You have the right to request that we delete personal information we have collected from you, subject to certain exceptions such as:
                    </p>
                    <ul className="text-gray-300 list-disc list-inside space-y-1">
                      <li>Completing transactions or providing requested services</li>
                      <li>Detecting and protecting against security incidents or fraud</li>
                      <li>Complying with legal obligations</li>
                      <li>Exercising free speech or ensuring another's right to free speech</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🔄</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-purple-400">Right to Portability</h3>
                    <p className="text-gray-300 leading-relaxed">
                      You have the right to request a copy of your personal information in a portable and, to the extent technically feasible, readily usable format that allows you to transmit the data to another entity without hindrance.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-orange-400">Right to Opt-Out of Sale</h3>
                    <p className="text-gray-300 leading-relaxed mb-3">
                      You have the right to opt out of the sale of your personal information. Under the CCPA, "sale" includes sharing personal information with third parties for monetary or other valuable consideration.
                    </p>
                    <Link 
                      to="/do-not-sell" 
                      className="inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold transition-colors"
                    >
                      Do Not Sell My Data
                    </Link>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">⚖️</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-[#9ec6f0]">Right to Non-Discrimination</h3>
                    <p className="text-gray-300 leading-relaxed">
                      You have the right not to receive discriminatory treatment for exercising your CCPA rights. We will not deny you goods or services, charge different prices, or provide different quality of service based on your exercise of these rights.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* How to Exercise Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">How to Exercise Your Rights</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-6">
                To exercise your CCPA rights, you can contact us using the following methods:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Email Request</h4>
                  <p className="text-gray-300 text-sm mb-2">
                    Send your request to our dedicated privacy email:
                  </p>
                  <a 
                    href="mailto:datarequest@electricnow.tv" 
                    className="text-[#9ec6f0] hover:text-[#b3cfed] underline font-semibold"
                  >
                    datarequest@electricnow.tv
                  </a>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Contact Form</h4>
                  <p className="text-gray-300 text-sm mb-2">
                    Use our contact form for privacy requests:
                  </p>
                  <Link 
                    to="/contact" 
                    className="text-[#9ec6f0] hover:text-[#b3cfed] underline font-semibold"
                  >
                    Contact Form
                  </Link>
                </div>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-400 mb-2">Verification Process</h4>
                <p className="text-gray-300 text-sm">
                  To protect your privacy, we will verify your identity before processing your request. This may involve confirming your email address or other identifying information associated with your account.
                </p>
              </div>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Categories of Information We Collect</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-6">
                Under the CCPA, we are required to disclose the categories of personal information we collect. Here are the categories we may collect:
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-600">
                      <th className="text-left py-3 px-4 text-[#9ec6f0]">Category</th>
                      <th className="text-left py-3 px-4 text-[#9ec6f0]">Examples</th>
                      <th className="text-left py-3 px-4 text-[#9ec6f0]">Collected</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-700">
                      <td className="py-3 px-4 font-semibold">Identifiers</td>
                      <td className="py-3 px-4">Name, email address, IP address, device identifiers</td>
                      <td className="py-3 px-4 text-[#9ec6f0]">Yes</td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 px-4 font-semibold">Personal Information</td>
                      <td className="py-3 px-4">Contact information, payment information</td>
                      <td className="py-3 px-4 text-[#9ec6f0]">Yes</td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 px-4 font-semibold">Commercial Information</td>
                      <td className="py-3 px-4">Viewing history, preferences, subscription details</td>
                      <td className="py-3 px-4 text-[#9ec6f0]">Yes</td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 px-4 font-semibold">Internet Activity</td>
                      <td className="py-3 px-4">Browsing history, search history, interaction with website</td>
                      <td className="py-3 px-4 text-[#9ec6f0]">Yes</td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 px-4 font-semibold">Geolocation Data</td>
                      <td className="py-3 px-4">Approximate location based on IP address</td>
                      <td className="py-3 px-4 text-[#9ec6f0]">Yes</td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 px-4 font-semibold">Inferences</td>
                      <td className="py-3 px-4">Preferences, characteristics, behavior predictions</td>
                      <td className="py-3 px-4 text-[#9ec6f0]">Yes</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-semibold">Sensitive Personal Information</td>
                      <td className="py-3 px-4">Precise geolocation, biometric data</td>
                      <td className="py-3 px-4 text-red-400">No</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Response Timeline */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Response Timeline</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📝</span>
                  </div>
                  <h4 className="font-semibold mb-2 text-blue-400">Request Received</h4>
                  <p className="text-gray-300 text-sm">We acknowledge your request within 10 business days</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🔍</span>
                  </div>
                  <h4 className="font-semibold mb-2 text-yellow-400">Verification</h4>
                  <p className="text-gray-300 text-sm">We verify your identity to protect your privacy</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">✅</span>
                  </div>
                  <h4 className="font-semibold mb-2 text-[#9ec6f0]">Response</h4>
                  <p className="text-gray-300 text-sm">We respond within 45 days (extendable to 90 days)</p>
                </div>
              </div>
            </div>
          </section>

          {/* Additional Resources */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Additional Resources</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-[#9ec6f0]">Related Pages</h3>
                <div className="space-y-2">
                  <Link to="/privacy-policy" className="block text-[#9ec6f0] hover:text-[#b3cfed] underline">
                    Full Privacy Policy
                  </Link>
                  <Link to="/do-not-sell" className="block text-[#9ec6f0] hover:text-[#b3cfed] underline">
                    Do Not Sell My Data
                  </Link>
                  <Link to="/terms-of-use" className="block text-[#9ec6f0] hover:text-[#b3cfed] underline">
                    Terms of Use
                  </Link>
                  <Link to="/contact" className="block text-[#9ec6f0] hover:text-[#b3cfed] underline">
                    Contact Us
                  </Link>
                </div>
              </div>
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-[#9ec6f0]">External Resources</h3>
                <div className="space-y-2">
                  <a 
                    href="https://oag.ca.gov/privacy/ccpa" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    California Attorney General CCPA Info
                  </a>
                  <a 
                    href="https://cppa.ca.gov/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    California Privacy Protection Agency
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="text-center">
            <div className="bg-teal-900/20 border border-[#2f6eb0]/30 rounded-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-[#9ec6f0]">Questions About Your Rights?</h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                If you have questions about your California privacy rights or need assistance exercising them, we're here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="mailto:datarequest@electricnow.tv" 
                  className="bg-[#2f6eb0] hover:bg-[#3d7fcb] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Email Privacy Team
                </a>
                <Link 
                  to="/contact" 
                  className="bg-[#1e4a7a] hover:bg-[#2f6eb0] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Contact Form
                </Link>
              </div>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CCPAPage;

